import React from "react";
import CountUp from "react-countup";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface StatCardProps {
  icon?: React.ReactNode;
  value: number | string;
  label: string;
  trend?: { value: string; positive: boolean };
  /** Optional one-line context under the value. */
  hint?: string;
  /** @deprecated The design system keeps stat icons monochrome; kept for compatibility. */
  iconColor?: string;
  /** @deprecated See iconColor. */
  iconBg?: string;
  decimals?: number;
  className?: string;
}

export default function StatCard({
  icon,
  value,
  label,
  trend,
  hint,
  decimals = 0,
  className,
}: StatCardProps) {
  const isNumericValue = typeof value === "number";

  return (
    <div
      className={cn(
        "flex h-full min-w-0 flex-col gap-3 rounded-xl border border-border bg-card p-5 shadow-soft transition-shadow hover:shadow-panel md:p-6",
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <p className="min-w-0 text-pretty text-sm text-muted-foreground">{label}</p>
        {icon && (
          <span
            className="flex size-10 shrink-0 items-center justify-center rounded-full bg-muted text-body-strong [&_svg]:size-[18px]"
            aria-hidden
          >
            {icon}
          </span>
        )}
      </div>
      <p
        className={cn(
          "truncate font-medium tracking-[-0.8px] text-foreground tabular-nums",
          isNumericValue ? "text-[28px] leading-9 md:text-[32px] md:leading-10" : "text-xl leading-8",
        )}
      >
        {isNumericValue ? (
          <CountUp end={value} duration={0.9} separator="," decimals={decimals} preserveValue />
        ) : (
          value
        )}
      </p>
      {(trend || hint) && (
        <div className="flex flex-wrap items-center gap-2 text-caption text-muted-foreground">
          {trend && (
            <span
              className={cn(
                "inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 font-medium",
                trend.positive
                  ? "bg-pastel-mint text-pastel-mint-ink"
                  : "bg-pastel-rose text-pastel-rose-ink",
              )}
            >
              {trend.positive ? (
                <ArrowUpRight className="size-3.5" aria-hidden />
              ) : (
                <ArrowDownRight className="size-3.5" aria-hidden />
              )}
              {trend.value}
            </span>
          )}
          {hint ?? (trend ? "vs previous period" : null)}
        </div>
      )}
    </div>
  );
}
